import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptionsMode } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { getStripeKey } from '#config/stripe';

import { useLocale } from '#src/app/shared/hooks/l10n';
import { getDefaultStripeAppearance } from '#src/app/shared/utils/payments';

import { toStripeLocale } from '../utils/toStripeLocale';

type Props = {
	options: StripeElementsOptionsMode;
	appearance?: StripeElementsOptionsMode['appearance'];
};

export const StripePaymentElementsProvider: React.FC<PropsWithChildren<Props>> = ({
	options,
	appearance,
	children,
}) => {
	const utilityContext = useUtilityContext();
	const environment = utilityContext.environment.getApiEnvironment();
	const subEnvironment =
		utilityContext.environment.getApiDemoEnvironmentTarget() ?? utilityContext.environment.getApiSubEnvironment();
	const stripeKey = getStripeKey({ environment, subEnvironment });
	const locale = useLocale();
	// Memoize the Stripe promise so it isn't recreated on every render. `async` is required
	// by @typescript-eslint/promise-function-async; the outer async function auto-unwraps
	// the returned Promise so the resolved type is still Promise<Stripe | null>.
	// eslint-disable-next-line react-hooks/use-memo
	const stripePromise = useMemo(async () => loadStripe(stripeKey), [stripeKey]);

	return (
		// mode is immutable after mount (not in StripeElementsUpdateOptions); keying on it
		// forces a remount when the user switches payment term (one-time ↔ recurring).
		// locale is also immutable after mount — runtime locale changes won't update the iframe.
		// amount is NOT keyed here: react-stripe-js calls elements.update({ amount }) on every
		// options.amount change, which Stripe propagates to wallet UIs (Google Pay, Apple Pay).
		<Elements
			key={options.mode}
			stripe={stripePromise}
			options={{
				...options,
				locale: toStripeLocale(locale),
				paymentMethodCreation: 'manual',
				appearance: appearance ?? getDefaultStripeAppearance(),
			}}
		>
			{children}
		</Elements>
	);
};
